import type { ReactNode } from "react";
import type { ColumnsType } from "antd/es/table";
import type {
  FilterType,
  FilterValue,
} from "@/components/UI/TableToolbar/Filtering/filterTypes";
import {
  ColumnVisibility,
  type ColumnsType as DashboardColumnsType,
  type TableState,
  type TableStateFunctions,
} from "@/types/tableState.types";
import type { SortItem } from "@/components/UI/TableToolbar/Sorting/types";
import DashboardTableHeader, {
  type DashboardFilterMetadata,
} from "./DashboardTableHeader";

type DashboardTableColumns = ColumnsType & {
  key?: string | number;
};

// Kill switch for the in-header column controls. Set to false to ship the plain
// column titles from AdvancedFiltersColumnConfig instead; filtering and sorting
// stay available through TableToolbar.
const HEADER_CONTROLS_ENABLED = false;

type DecorateDashboardColumnsOptions = {
  filterMetadata: Record<string, DashboardFilterMetadata>;
  currentFilters: TableState["filters"];
  setFilters: TableStateFunctions["setFilters"];
  sorting: SortItem[];
  pendingSorting: SortItem[] | null;
  toggleSorting: (field: string) => void;
  columnsTitleCreator: (column: string) => string;
  dashboardColumns: DashboardColumnsType[];
  setDashboardColumns: (columns: DashboardColumnsType[]) => void;
};

export const decorateDashboardColumns = <T extends object>(
  columns: ColumnsType<T>,
  {
    filterMetadata,
    currentFilters,
    setFilters,
    sorting,
    pendingSorting,
    toggleSorting,
    columnsTitleCreator,
    dashboardColumns,
    setDashboardColumns,
  }: DecorateDashboardColumnsOptions,
): ColumnsType<T> =>
  columns.map((column) => {
    if (!HEADER_CONTROLS_ENABLED) return column;

    const dashboardColumn = column as DashboardTableColumns;
    const field =
      typeof dashboardColumn.key === "string" ? dashboardColumn.key : undefined;

    if (!field) return column;

    const stateColumn = dashboardColumns.find(
      (candidate) => candidate.fieldName === field,
    );
    const canHide = Boolean(stateColumn && !stateColumn.mandatory);

    return {
      ...column,
      title: (
        <DashboardTableHeader
          field={field}
          label={columnsTitleCreator(field)}
          filterMetadata={filterMetadata[field]}
          currentFilters={currentFilters}
          setFilters={setFilters}
          sorting={sorting}
          pendingSorting={pendingSorting}
          onSort={() => toggleSorting(field)}
          canHide={canHide}
          onHide={() => {
            if (!canHide) return;

            setDashboardColumns(
              dashboardColumns.map((candidate) =>
                candidate.fieldName === field
                  ? { ...candidate, visibility: ColumnVisibility.HIDDEN }
                  : candidate,
              ),
            );
          }}
        />
      ) as ReactNode,
    };
  });

export type {
  DashboardFilterMetadata,
  DecorateDashboardColumnsOptions,
  FilterType,
  FilterValue,
};
